import { useMemo, useReducer } from 'react';
import type { DietPlanApi } from '../api/dietPlanApi';
import { mealTargets, status, totals } from '../lib/nutrition';
import { Action, BuilderState, builderReducer, initialState } from '../state/builderReducer';
import type { Id, Meal, Plan, StatusInfo, Targets } from '../types';
import { planTipsOverLimit } from '../types';
import { useKaira } from './useKaira';

export type Dispatch = (a: Action) => void;

export type MealStat = {
  m: Meal;
  T: Targets;
  opts: (StatusInfo & { totals: Targets })[];
};

export type BuilderDerived = {
  stats: MealStat[];
  active: MealStat[];
  offOpts: number;
  okMeals: number;
  totalOpts: number;
  shareSum: number;
  empties: number;
  /** Active meals where every option has no foods yet. */
  mealsWithoutFood: number;
  issues: { name: string; bad: number; all: number; empty: number }[];
  tipsOverLimit: boolean;
  blocked: boolean;
  master: boolean;
};

export type Builder = {
  s: BuilderState;
  dispatch: Dispatch;
  api: DietPlanApi;
  derived: BuilderDerived | null;
  kaira: ReturnType<typeof import('./useKaira').useKaira>;
};

export function derivePlan(plan: Plan | null): BuilderDerived | null {
  if (!plan) return null;
  const stats: MealStat[] = plan.meals.map((m) => {
    const T = mealTargets(plan, m);
    const opts = m.options.map((o) => {
      const st = status(o.foods, T);
      return { ...st, totals: totals(o.foods) };
    });
    return { m, T, opts };
  });
  const active = stats.filter((s) => !s.m.skip);
  const shareSum = Math.round(active.reduce((a, s) => a + (+s.m.share || 0), 0) * 10) / 10;
  const issues = active
    .map((s) => {
      const empty = s.opts.filter((o) => o.st === 'empty').length;
      const bad = s.opts.filter((o) => o.st === 'warn' || o.st === 'bad' || o.st === 'empty').length;
      return { name: s.m.name, bad, all: s.opts.length, empty };
    })
    .filter((i) => i.bad > 0);
  const empties = issues.reduce((a, i) => a + i.empty, 0);
  const mealsWithoutFood = active.filter((s) => s.opts.every((o) => o.st === 'empty')).length;
  const offOpts = issues.reduce((a, i) => a + i.bad, 0);
  const tipsOverLimit = planTipsOverLimit(plan);
  return {
    stats,
    active,
    offOpts,
    okMeals: active.filter((s) => s.opts.length && s.opts.every((o) => o.st === 'ok')).length,
    totalOpts: active.reduce((a, s) => a + s.opts.length, 0),
    shareSum,
    empties,
    mealsWithoutFood,
    issues,
    tipsOverLimit,
    blocked: empties > 0 || shareSum !== 100 || tipsOverLimit,
    master: !!plan.templateId,
  };
}

export function useDietPlanBuilder(patientId: Id | null, api: DietPlanApi): Builder {
  const [s, dispatch] = useReducer(builderReducer, patientId, initialState);
  const derived = useMemo(() => derivePlan(s.plan), [s.plan]);
  const kaira = useKaira();
  return { s, dispatch, api, derived, kaira };
}
